/**
 * BotChatTestPage — shared test form for the provider-based bot channels
 * (Viber, LINE — roadmap #77). Recipients are the platform user ids of users
 * who opted in to the account's bot; message is CUSTOM text only for now
 * (templates arrive with the templates roadmap, groups later).
 */

import React, { useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { Box, Grid, IconButton, MenuItem, TextField } from "@mui/material";
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { sendBotChatTestNotification } from "../services/testService";
import AccountAppPicker from "./sections/AccountAppPicker";
import RgSection from "./sections/RgSection";
import TestPageShell from "./TestPageShell";

interface BotChatTestPageProps {
  channel: "viber" | "line";
}

const CHANNEL_KEYS: Record<
  BotChatTestPageProps["channel"],
  { title: string; sent: string; failed: string; userIdHelp: string }
> = {
  viber: {
    title: "resources.test.viber.title",
    sent: "resources.test.notifications.viberSent",
    failed: "resources.test.notifications.viberSendFailed",
    userIdHelp: "resources.test.botChat.viberUserIdHelp",
  },
  line: {
    title: "resources.test.line.title",
    sent: "resources.test.notifications.lineSent",
    failed: "resources.test.notifications.lineSendFailed",
    userIdHelp: "resources.test.botChat.lineUserIdHelp",
  },
};

interface BotChatFormValues {
  accountId: string;
  appId: string;
  userIds: { value: string }[];
  text: string;
  messagePriority: "STANDARD" | "CRITICAL";
  webhookUrl: string;
}

const emptyValues = (): BotChatFormValues => ({
  accountId: "",
  appId: "",
  userIds: [{ value: "" }],
  text: "",
  messagePriority: "STANDARD",
  webhookUrl: "",
});

export const BotChatTestPage: React.FC<BotChatTestPageProps> = ({
  channel,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const keys = CHANNEL_KEYS[channel];

  const methods = useForm<BotChatFormValues>({
    defaultValues: emptyValues(),
    mode: "all",
    reValidateMode: "onChange",
    shouldFocusError: true,
  });
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "userIds",
  });

  const onSubmit = async (values: BotChatFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      const response = await sendBotChatTestNotification(channel, {
        accountId: values.accountId,
        appId: values.appId,
        recipients: {
          type: "INDIVIDUAL",
          userIds: values.userIds.map((u) => u.value.trim()).filter(Boolean),
        },
        message: { type: "CUSTOM", content: values.text },
        messageType:
          values.messagePriority === "CRITICAL" ? "critical" : "normal",
        webhookUrl: values.webhookUrl.trim() || undefined,
        origin: "admin-test",
      });
      if (response.success) {
        notify(keys.sent, { type: "success" });
        methods.reset(emptyValues());
      } else {
        notify(response.message ?? translate(keys.failed), { type: "error" });
      }
    } catch (err: unknown) {
      notify(extractErrorMessage(err, keys.failed, translate), {
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <TestPageShell
        titleKey={keys.title}
        submitting={submitting}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <RgSection
          label={translate("resources.test.sections.accountApplication", {
            _: "Account & application",
          })}
        >
          <AccountAppPicker />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.recipients", {
            _: "Recipients",
          })}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12}>
              {fields.map((f, idx) => (
                <Box
                  key={f.id}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: idx < fields.length - 1 ? 1.5 : 0,
                  }}
                >
                  <Controller
                    name={`userIds.${idx}.value`}
                    control={methods.control}
                    rules={{
                      required: translate(
                        "resources.test.botChat.userIdRequired",
                      ),
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        label={`${translate("resources.test.botChat.userId")} ${fields.length > 1 ? idx + 1 : ""}`.trim()}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={
                          fieldState.error?.message ?? translate(keys.userIdHelp)
                        }
                      />
                    )}
                  />
                  {fields.length > 1 && (
                    <IconButton
                      aria-label={translate("ra.action.remove")}
                      onClick={() => remove(idx)}
                      sx={{ alignSelf: "center" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  {idx === fields.length - 1 && fields.length < 20 && (
                    <IconButton
                      aria-label={translate("ra.action.add")}
                      onClick={() => append({ value: "" })}
                      sx={{ alignSelf: "center" }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Grid>
          </Grid>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.message", {
            _: "Message",
          })}
        >
          <Controller
            name="text"
            control={methods.control}
            rules={{
              required: translate("resources.test.botChat.textRequired"),
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                required
                multiline
                minRows={3}
                label={translate("resources.test.botChat.textField")}
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.delivery", {
            _: "Delivery options",
          })}
          last
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="messagePriority"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label={translate("resources.test.fields.messagePriority")}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="STANDARD">
                      {translate(
                        "resources.test.choices.messagePriority.STANDARD",
                      )}
                    </MenuItem>
                    <MenuItem value="CRITICAL">
                      {translate(
                        "resources.test.choices.messagePriority.CRITICAL",
                      )}
                    </MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="webhookUrl"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.fields.webhookUrl")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>
      </TestPageShell>
    </FormProvider>
  );
};

export default BotChatTestPage;
